import { isAuthenticated } from '../utils/authHelper';
import React, { Component, Fragment } from 'react';
import MuiThemeProvider from 'material-ui/styles/MuiThemeProvider';
import ReactTable from 'react-table';
import matchSorter from 'match-sorter';
import 'react-table/react-table.css';
import moment from 'moment';
import { browserHistory } from 'react-router';

import { 
  getAllCities, 
  getCityRequests, 
  createCityRequest, 
  updateCityRequest, 
  editPendingRequest,
  getPendingRequestForCity,
  approveCityRequest, 
  rejectCityRequest,
  getAllStates,
  createState
} from '../utils/CityApiUtils';
import CityRequestTimeline from './CityRequestTimeline';
import Header from './Header';
import userdata from '../config/jwt_token';

import {
  Button, Tabs, Tab, Dialog, DialogTitle, DialogContent, 
  DialogActions, TextField, MenuItem, Select, InputLabel, 
  FormControl, Typography, Paper, FormControlLabel, Checkbox,
  Snackbar
} from '@material-ui/core';
import CircularProgress from 'material-ui/CircularProgress';

import EditIcon from '@material-ui/icons/Edit';
import HistoryIcon from '@material-ui/icons/History';
import AddIcon from '@material-ui/icons/Add';

class CityManagement extends Component {
  constructor(props) {
    super(props);
    if (!isAuthenticated()) {
      browserHistory.push('/');
    }
    this.state = {
      tabValue: 0,
      cities: [],
      requests: [],
      states: [],
      loading: false,
      
      // Dialog states
      requestDialogOpen: false,
      approvalDialogOpen: false,
      rejectionDialogOpen: false,
      timelineOpen: false,
      
      // Form data
      selectedRequest: null,
      formData: {
        city_id: null,
        city_name: '',
        state_id: '',
        state_name: '',
        bank_account_number: '',
        remarks: ''
      },
      approvalRemarks: '',
      rejectionRemarks: '',
      
      // Edit pending request mode
      isEditingPendingRequest: false,
      pendingRequestId: null,
      
      // New state creation
      isCreatingNewState: false,
      newStateName: '',
      
      // Snackbar
      snackOpen: false,
      snackText: '',
      
      role: userdata ? userdata.role : ''
    };
  }

  componentDidMount() {
    this.fetchInitialData();
  }

  fetchInitialData = async () => {
    this.setState({ loading: true });
    try {
      await this.fetchCities();
      await this.fetchRequests();
      await this.fetchStates();
      
      this.setState({ loading: false });
    } catch (err) {
      console.error('Error fetching initial data', err);
      this.setState({ loading: false });
    }
  };

  fetchStates = async () => {
    try {
      const res = await getAllStates();
      if (res.status === 'success') {
        this.setState({ states: res.data });
      }
    } catch (err) {
      console.error('Error fetching states', err);
    }
  };

  showSnack = (message) => {
    this.setState({ snackOpen: true, snackText: message });
  };

  handleCloseSnack = () => {
    this.setState({ snackOpen: false });
  };

  fetchCities = async () => {
    try {
      const res = await getAllCities();
      if (res.status === 'success') {
        this.setState({ cities: res.data });
      }
    } catch (err) {
      console.error(err);
    }
  };

  fetchRequests = async () => {
    try {
      const res = await getCityRequests('all');
      if (res.status === 'success') {
        this.setState({ requests: res.data });
      }
    } catch (err) {
      console.error(err);
    }
  };

  handleTabChange = (event, newValue) => {
    this.setState({ tabValue: newValue });
    if (newValue === 0) {
      this.fetchCities();
    } else {
      this.fetchRequests();
    }
  };

  // Request Handlers
  openCreateRequestDialog = () => {
    this.setState({
      requestDialogOpen: true,
      formData: { city_id: null, city_name: '', state_id: '', state_name: '', bank_account_number: '', remarks: '' },
      isCreatingNewState: false,
      newStateName: '',
      isEditingPendingRequest: false,
      pendingRequestId: null
    });
  };

  openUpdateRequestDialog = async (city) => {
    this.setState({ loading: true });
    
    try {
      // Check if there's a pending request for this city
      const pendingRes = await getPendingRequestForCity({ city_id: city.id });
      
      if (pendingRes.hasPending && pendingRes.data) {
        // There's a pending request - open in edit mode
        const pendingRequest = pendingRes.data;
        this.setState({
          requestDialogOpen: true,
          formData: { 
            city_id: city.id, 
            city_name: pendingRequest.city_name, 
            state_id: pendingRequest.state_id, 
            state_name: pendingRequest.state ? pendingRequest.state.state_name : '',
            bank_account_number: pendingRequest.bank_account_number || '',
            remarks: pendingRequest.city_finance_remarks || '' 
          },
          isCreatingNewState: false,
          newStateName: '',
          isEditingPendingRequest: true,
          pendingRequestId: pendingRequest.request_id,
          loading: false
        });
        this.showSnack('Editing existing pending request for this city');
      } else {
        // No pending request - create new update request
        this.setState({
          requestDialogOpen: true,
          formData: { 
            city_id: city.id, 
            city_name: city.city_name, 
            state_id: city.state_id, 
            state_name: city.state_details ? city.state_details.state_name : '',
            bank_account_number: city.bank_account_number || '',
            remarks: '' 
          },
          isCreatingNewState: false,
          newStateName: '',
          isEditingPendingRequest: false,
          pendingRequestId: null,
          loading: false
        });
      }
    } catch (err) {
      console.error('Error checking pending request', err);
      // Fallback to normal create update request
      this.setState({
        requestDialogOpen: true,
        formData: { 
          city_id: city.id, 
          city_name: city.city_name, 
          state_id: city.state_id, 
          state_name: city.state_details ? city.state_details.state_name : '',
          bank_account_number: city.bank_account_number || '',
          remarks: '' 
        },
        isCreatingNewState: false,
        newStateName: '',
        isEditingPendingRequest: false,
        pendingRequestId: null,
        loading: false
      });
    }
  };

  handleRequestSubmit = async () => {
    const { formData, isCreatingNewState, newStateName, isEditingPendingRequest, pendingRequestId } = this.state;
    
    // Validate
    if (!formData.city_name.trim()) {
      this.showSnack('City name is required');
      return;
    }
    
    if (isCreatingNewState && !newStateName.trim()) {
      this.showSnack('State name is required');
      return;
    }
    
    if (!isCreatingNewState && !formData.state_id) {
      this.showSnack('Please select a state or create a new one');
      return;
    }

    if (!formData.bank_account_number.trim()) {
      this.showSnack('Bank account number is required');
      return;
    }
    
    this.setState({ loading: true });
    try {
      let stateId = formData.state_id;
      let stateName = formData.state_name;
      
      // If creating new state, create it first
      if (isCreatingNewState) {
        const stateRes = await createState({ state_name: newStateName.trim() });
        if (stateRes.status === 'success') {
          stateId = stateRes.data.id;
          stateName = newStateName.trim();
          // Refresh states list
          await this.fetchStates();
        } else {
          this.showSnack(stateRes.message || 'Error creating state');
          this.setState({ loading: false });
          return;
        }
      }
      
      const requestData = { ...formData, state_id: stateId, state_name: stateName };
      
      let res;
      if (isEditingPendingRequest && pendingRequestId) {
        // Edit existing pending request
        res = await editPendingRequest({ ...requestData, request_id: pendingRequestId });
      } else if (formData.city_id) {
        // Create new update request for existing city
        res = await updateCityRequest(requestData);
      } else {
        // Create new city request
        res = await createCityRequest(requestData);
      }
      
      if (res.status === 'success') {
        this.setState({ 
          requestDialogOpen: false, 
          loading: false, 
          isCreatingNewState: false, 
          newStateName: '',
          isEditingPendingRequest: false,
          pendingRequestId: null
        });
        this.fetchRequests();
        if (isEditingPendingRequest) {
          this.showSnack('Pending request updated successfully');
        } else if (this.state.tabValue === 0) {
          this.showSnack('Request submitted successfully');
        }
      } else {
        this.showSnack(res.message);
        this.setState({ loading: false });
      }
    } catch (err) {
      console.error(err);
      this.setState({ loading: false });
      this.showSnack('Error submitting request');
    }
  };

  // Approval/Rejection Handlers
  openApprovalDialog = (request) => {
    this.setState({
      approvalDialogOpen: true,
      selectedRequest: request,
      approvalRemarks: ''
    });
  };

  openRejectionDialog = (request) => {
    this.setState({
      rejectionDialogOpen: true,
      selectedRequest: request,
      rejectionRemarks: ''
    });
  };

  handleApprove = async () => {
    const { selectedRequest, approvalRemarks } = this.state;
    this.setState({ loading: true });
    try {
      const res = await approveCityRequest({ 
        request_id: selectedRequest.request_id, 
        remarks: approvalRemarks 
      });
      
      if (res.status === 'success') {
        this.setState({ approvalDialogOpen: false, loading: false });
        this.fetchRequests();
        this.fetchCities();
        this.showSnack('Request approved successfully');
      } else {
        this.showSnack(res.message);
        this.setState({ loading: false });
      }
    } catch (err) {
      console.error(err);
      this.setState({ loading: false });
      this.showSnack('Error approving request');
    }
  };

  handleReject = async () => {
    const { selectedRequest, rejectionRemarks } = this.state;
    if (!rejectionRemarks) {
      this.showSnack('Remarks are mandatory for rejection');
      return;
    }
    this.setState({ loading: true });
    try {
      const res = await rejectCityRequest({ 
        request_id: selectedRequest.request_id, 
        remarks: rejectionRemarks 
      });
      
      if (res.status === 'success') {
        this.setState({ rejectionDialogOpen: false, loading: false });
        this.fetchRequests();
        this.showSnack('Request rejected successfully');
      } else {
        this.showSnack(res.message);
        this.setState({ loading: false });
      }
    } catch (err) {
      console.error(err);
      this.setState({ loading: false });
      this.showSnack('Error rejecting request');
    }
  };

  // Edit pending request from requests table
  openEditPendingRequestDialog = (request) => {
    this.setState({
      requestDialogOpen: true,
      formData: { 
        city_id: request.city_id, 
        city_name: request.city_name, 
        state_id: request.state_id, 
        state_name: request.state ? request.state.state_name : '',
        bank_account_number: request.bank_account_number || '',
        remarks: request.city_finance_remarks || '' 
      },
      isCreatingNewState: false,
      newStateName: '',
      isEditingPendingRequest: true,
      pendingRequestId: request.request_id
    });
  };

  // Timeline
  openTimeline = (request) => {
    this.setState({
      timelineOpen: true,
      selectedRequest: request
    });
  };

  getCitiesColumns() {
    const { role } = this.state;
    const canRequestUpdate = ['City Finance', 'root'].includes(role);

    const columns = [
      {
        Header: "City ID",
        id: "id",
        accessor: d => d.id,
        filterMethod: (filter, rows) =>
          matchSorter(rows, filter.value, { keys: ["id"] }),
        filterAll: true,
        width: 100
      },
      {
        Header: "City Name",
        id: "city_name",
        accessor: d => d.city_name,
        style: { 'whiteSpace': 'unset', 'wordWrap': 'break-word' },
        filterMethod: (filter, rows) =>
          matchSorter(rows, filter.value, { keys: ["city_name"] }),
        filterAll: true
      },
      {
        Header: "State",
        id: "state_name",
        accessor: d => d.state_details ? d.state_details.state_name : '',
        filterMethod: (filter, rows) =>
          matchSorter(rows, filter.value, { keys: ["state_name"] }),
        filterAll: true
      },
      {
        Header: "Bank Account Number",
        id: "bank_account_number",
        accessor: d => d.bank_account_number || '-',
        filterMethod: (filter, rows) =>
          matchSorter(rows, filter.value, { keys: ["bank_account_number"] }),
        filterAll: true,
        width: 180
      }
    ];

    if (canRequestUpdate) {
      columns.push({
        Header: "Actions",
        id: "actions",
        accessor: d => d,
        filterable: false,
        sortable: false,
        width: 180,
        Cell: ({ value }) => (
          <Button 
            size="small"
            color="primary"
            onClick={() => this.openUpdateRequestDialog(value)}
          >
            <EditIcon fontSize="small" style={{ marginRight: 5 }} />
            Update Request
          </Button>
        )
      });
    }

    return columns;
  }

  getRequestsColumns() {
    const { role } = this.state;
    
    const canApprove = (req) => {
      if (req.status !== 'pending') return false;
      return ['Super Central Finance', 'root'].includes(role);
    };

    const canEdit = (req) => {
      if (req.status !== 'pending') return false;
      // City Finance can edit their own pending requests, root can edit any
      return ['City Finance', 'root'].includes(role);
    };

    const columns = [
      {
        Header: "ID",
        id: "request_id",
        accessor: d => d.request_id,
        filterMethod: (filter, rows) =>
          matchSorter(rows, filter.value, { keys: ["request_id"] }),
        filterAll: true,
        width: 80
      },
      {
        Header: "Type",
        id: "request_type",
        accessor: d => d.request_type,
        style: { textTransform: 'capitalize' },
        filterMethod: (filter, rows) =>
          matchSorter(rows, filter.value, { keys: ["request_type"] }),
        filterAll: true,
        width: 100
      },
      {
        Header: "City Name",
        id: "city_name",
        accessor: d => d.city_name,
        style: { 'whiteSpace': 'unset', 'wordWrap': 'break-word' },
        filterMethod: (filter, rows) =>
          matchSorter(rows, filter.value, { keys: ["city_name"] }),
        filterAll: true
      },
      {
        Header: "State",
        id: "state_name",
        accessor: d => d.state ? d.state.state_name : '',
        filterMethod: (filter, rows) =>
          matchSorter(rows, filter.value, { keys: ["state_name"] }),
        filterAll: true
      },
      {
        Header: "Bank Account",
        id: "bank_account_number",
        accessor: d => d.bank_account_number || '-',
        filterMethod: (filter, rows) =>
          matchSorter(rows, filter.value, { keys: ["bank_account_number"] }),
        filterAll: true,
        width: 150
      },
      {
        Header: "Requester Name",
        id: "requester_name",
        accessor: d => d.requester ? d.requester.name : '-',
        filterMethod: (filter, rows) =>
          matchSorter(rows, filter.value, { keys: ["requester_name"] }),
        filterAll: true,
        width: 120
      },
      {
        Header: "Status",
        id: "status",
        accessor: d => d.status,
        filterMethod: (filter, rows) =>
          matchSorter(rows, filter.value, { keys: ["status"] }),
        filterAll: true,
        width: 100,
        Cell: ({ value }) => (
          <span style={{
            padding: '4px 8px',
            borderRadius: '4px',
            backgroundColor: value === 'approved' ? '#4caf50' : value === 'rejected' ? '#f44336' : '#ff9800',
            color: 'white',
            fontSize: '12px'
          }}>
            {value ? value.toUpperCase() : 'N/A'}
          </span>
        )
      },
      {
        Header: "Created At",
        id: "created_at",
        accessor: d => moment(d.created_at).format("DD MMM YYYY HH:mm"),
        filterMethod: (filter, rows) =>
          matchSorter(rows, filter.value, { keys: ["created_at"] }),
        filterAll: true,
        width: 150
      },
      {
        Header: "Actions",
        id: "actions",
        accessor: d => d,
        filterable: false,
        sortable: false,
        minWidth: 300,
        Cell: ({ value }) => (
          <div>
            {canEdit(value) && (
              <Button 
                onClick={() => this.openEditPendingRequestDialog(value)} 
                color="default" 
                variant="outlined" 
                size="small" 
                style={{ marginRight: 5 }}
              >
                <EditIcon fontSize="small" style={{ marginRight: 3 }} />
                Edit
              </Button>
            )}
            {canApprove(value) && (
              <Fragment>
                <Button 
                  onClick={() => this.openApprovalDialog(value)} 
                  color="primary" 
                  variant="outlined" 
                  size="small" 
                  style={{ marginRight: 5 }}
                >
                  Approve
                </Button>
                <Button 
                  onClick={() => this.openRejectionDialog(value)} 
                  color="secondary" 
                  variant="outlined" 
                  size="small" 
                  style={{ marginRight: 5 }}
                >
                  Reject
                </Button>
              </Fragment>
            )}
            <Button 
              size="small"
              onClick={() => this.openTimeline(value)}
            >
              <HistoryIcon fontSize="small" style={{ marginRight: 3 }} />
              Timeline
            </Button>
          </div>
        )
      }
    ];

    return columns;
  }

  getFilteredRequests() {
    const { requests, tabValue } = this.state;
    
    if (tabValue === 1) {
      return requests.filter(r => r.status === 'pending');
    }
    return requests;
  }

  renderCitiesTable() {
    const { cities } = this.state;

    return (
      <ReactTable
        style={{ textAlign: "center", margin: '20px' }}
        data={cities}
        filterable
        defaultFilterMethod={(filter, row) =>
          String(row[filter.id]) === filter.value}
        columns={[{ columns: this.getCitiesColumns() }]}
        defaultPageSize={10}
        className="-striped -highlight"
        noDataText="No Cities Found!"
      />
    );
  }

  renderRequestsTable() {
    const filteredRequests = this.getFilteredRequests();

    return (
      <ReactTable
        style={{ textAlign: "center", margin: '20px' }}
        data={filteredRequests}
        filterable
        defaultFilterMethod={(filter, row) =>
          String(row[filter.id]) === filter.value}
        columns={[{ columns: this.getRequestsColumns() }]}
        defaultPageSize={10}
        className="-striped -highlight"
        noDataText="No Requests Found!"
        // getTrProps={(state, rowInfo) => {
        //   if (rowInfo && rowInfo.row) {
        //     const status = rowInfo.original.status;
        //     return {
        //       style: {
        //         color: status === 'approved' ? 'green' : status === 'rejected' ? '#FF6347' : 'inherit'
        //       }
        //     };
        //   }
        //   return null;
        // }}
      />
    );
  }

  render() {
    const { role, tabValue, loading, requestDialogOpen, formData, states, approvalDialogOpen, rejectionDialogOpen, timelineOpen, selectedRequest } = this.state;
    const canCreate = ['City Finance', 'root'].includes(role);

    return (
      <MuiThemeProvider>
        <div>
          <Header />
          <div style={{ padding: 20 }}>
            {canCreate && (
              <Button 
                variant="contained" 
                color="primary" 
                onClick={this.openCreateRequestDialog}
                style={{ marginBottom: 20 }}
              >
                <AddIcon style={{ marginRight: 5 }} />
                Request New City
              </Button>
            )}

            <Paper style={{ marginBottom: 20 }}>
              <Tabs value={tabValue} onChange={this.handleTabChange} indicatorColor="primary" textColor="primary">
                <Tab label="All Cities" />
                <Tab label="Pending Requests" />
                <Tab label="All Requests / History" />
              </Tabs>
            </Paper>

            {loading && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <CircularProgress size={60} thickness={7} />
              </div>
            )}

            {!loading && tabValue === 0 && this.renderCitiesTable()}
            {!loading && tabValue === 1 && this.renderRequestsTable()}
            {!loading && tabValue === 2 && this.renderRequestsTable()}

            {/* Create/Update Dialog */}
            <Dialog open={requestDialogOpen} onClose={() => this.setState({ requestDialogOpen: false, isEditingPendingRequest: false, pendingRequestId: null })} maxWidth="sm" fullWidth>
              <DialogTitle>
                {this.state.isEditingPendingRequest 
                  ? 'Edit Pending Request' 
                  : (formData.city_id ? 'Update City Request' : 'New City Request')}
              </DialogTitle>
              <DialogContent>
                {this.state.isEditingPendingRequest && (
                  <Paper style={{ 
                    padding: '10px 15px', 
                    marginBottom: '15px', 
                    backgroundColor: '#fff3e0',
                    border: '1px solid #ff9800'
                  }}>
                    <Typography variant="body2" style={{ color: '#e65100' }}>
                      <strong>Note:</strong> You are editing an existing pending request (ID: {this.state.pendingRequestId}). 
                      Changes will update the pending request instead of creating a new one.
                    </Typography>
                  </Paper>
                )}
                <TextField
                  autoFocus
                  margin="dense"
                  label="City Name"
                  fullWidth
                  value={formData.city_name}
                  onChange={(e) => this.setState({ formData: { ...formData, city_name: e.target.value } })}
                />
                {/* Only show Create New State option for new city requests */}
                {!formData.city_id && !this.state.isEditingPendingRequest && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={this.state.isCreatingNewState}
                        onChange={(e) => this.setState({ 
                          isCreatingNewState: e.target.checked,
                          formData: { ...formData, state_id: '' }
                        })}
                        color="primary"
                      />
                    }
                    label="Create New State"
                    style={{ marginTop: 10 }}
                  />
                )}
                
                {!this.state.isCreatingNewState ? (
                  <FormControl fullWidth margin="dense">
                    <InputLabel>State</InputLabel>
                    <Select
                      value={formData.state_id}
                      onChange={(e) => this.setState({ formData: { ...formData, state_id: e.target.value, state_name: states.find(state => state.id === e.target.value).state_name } })}
                    >
                      {states.map(state => (
                        <MenuItem key={state.id} value={state.id}>{state.state_name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <TextField
                    margin="dense"
                    label="New State Name"
                    fullWidth
                    value={this.state.newStateName}
                    onChange={(e) => this.setState({ newStateName: e.target.value })}
                    placeholder="Enter new state name"
                    helperText="This will create a new state in the system"
                  />
                )}
                <TextField
                  margin="dense"
                  label="Bank Account Number (Debit Account)"
                  fullWidth
                  value={formData.bank_account_number}
                  onChange={(e) => this.setState({ formData: { ...formData, bank_account_number: e.target.value } })}
                  placeholder="Enter bank account number for this city"
                  helperText="This account will be used as Debit Account for payments in this city"
                />
                <TextField
                  margin="dense"
                  label="Remarks"
                  fullWidth
                  multiline
                  rows={3}
                  value={formData.remarks}
                  onChange={(e) => this.setState({ formData: { ...formData, remarks: e.target.value } })}
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={() => this.setState({ requestDialogOpen: false })} disabled={loading}>Cancel</Button>
                <Button onClick={this.handleRequestSubmit} color="primary" disabled={loading}>
                  {loading ? <CircularProgress size={20} thickness={5} /> : 'Submit'}
                </Button>
              </DialogActions>
            </Dialog>

            {/* Approval Dialog */}
            <Dialog open={approvalDialogOpen} onClose={() => this.setState({ approvalDialogOpen: false })} maxWidth="sm" fullWidth>
              <DialogTitle>Approve Request</DialogTitle>
              <DialogContent>
                <Typography gutterBottom>
                  Are you sure you want to approve request for <strong>{selectedRequest ? selectedRequest.city_name : ''}</strong>?
                </Typography>
                <TextField
                  margin="dense"
                  label="Approval Remarks (Optional)"
                  fullWidth
                  multiline
                  rows={3}
                  value={this.state.approvalRemarks}
                  onChange={(e) => this.setState({ approvalRemarks: e.target.value })}
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={() => this.setState({ approvalDialogOpen: false })} disabled={loading}>Cancel</Button>
                <Button onClick={this.handleApprove} color="primary" variant="contained" disabled={loading}>
                  {loading ? <CircularProgress size={20} thickness={5} style={{ color: 'white' }} /> : 'Approve'}
                </Button>
              </DialogActions>
            </Dialog>

            {/* Rejection Dialog */}
            <Dialog open={rejectionDialogOpen} onClose={() => this.setState({ rejectionDialogOpen: false })} maxWidth="sm" fullWidth>
              <DialogTitle>Reject Request</DialogTitle>
              <DialogContent>
                <Typography gutterBottom>
                  Are you sure you want to reject request for <strong>{selectedRequest ? selectedRequest.city_name : ''}</strong>?
                </Typography>
                <TextField
                  margin="dense"
                  label="Rejection Remarks (Mandatory)"
                  fullWidth
                  multiline
                  rows={3}
                  value={this.state.rejectionRemarks}
                  onChange={(e) => this.setState({ rejectionRemarks: e.target.value })}
                  error={this.state.rejectionRemarks === ''}
                  helperText={this.state.rejectionRemarks === '' ? 'Remarks are required' : ''}
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={() => this.setState({ rejectionDialogOpen: false })} disabled={loading}>Cancel</Button>
                <Button onClick={this.handleReject} color="secondary" variant="contained" disabled={loading}>
                  {loading ? <CircularProgress size={20} thickness={5} style={{ color: 'white' }} /> : 'Reject'}
                </Button>
              </DialogActions>
            </Dialog>

            {/* Timeline Dialog */}
            <CityRequestTimeline 
              open={timelineOpen} 
              requestId={selectedRequest ? selectedRequest.request_id : null} 
              onClose={() => this.setState({ timelineOpen: false })} 
            />

            {/* Snackbar for notifications */}
            <Snackbar
              open={this.state.snackOpen}
              autoHideDuration={3000}
              onClose={this.handleCloseSnack}
              message={this.state.snackText}
            />
          </div>
        </div>
      </MuiThemeProvider>
    );
  }
}

export default CityManagement;



// WEBPACK FOOTER //
// ./src/components/CityManagement.js